/** Two editions of one market's factor set, side by side - **gate 20, ADR-0120 option B**.
 *
 * A **view**. It shows the rows that differ between an edition the library keeps for history and
 * the edition that prices new figures, with **both values as the rows hold them and no computed
 * difference**: subtracting two published figures is arithmetic this product would have to own and
 * round. It does not choose which edition prices anything, and it says so, because a comparison
 * that looked like a choice would be read as one.
 *
 * Which pairs exist comes from the library's own data, never from a list here: within each
 * jurisdiction, every set that no longer prices new figures (`prices_new_figures`) is compared with
 * the one that does. A jurisdiction with one set shows nothing - there is nothing to compare, and
 * the earlier screen said so. The comparison is fetched only when a reader opens it.
 */

import { useState } from "react";

import { useFactorComparison } from "../api/factorEditions";
import type { FactorComparedRow } from "../api/factorEditions";
import type { FactorLibrarySet } from "../api/factors";
import { ReadFailed } from "../components/Loaded";
import { categoryLabel } from "../lib/categoryLabel";
import { factorValue } from "../lib/format";
import own from "./FactorLibrary.module.css";
import { editionPairs } from "./factorLibraryView";
import styles from "./Screen.module.css";

const OUTCOME_WORDS: Record<FactorComparedRow["outcome"], string> = {
  changed: "Differs",
  only_in_to: "Added",
  only_in_from: "Not carried forward",
};

const CHANGE_WORDS: Record<string, string> = {
  value: "value",
  unit_out: "label (CO₂e or CO₂ only)",
  components: "per-gas parts",
  external_id: "publisher’s id",
};

function Side({ value, unit }: { value: string | null; unit: string | null }) {
  if (value === null) {
    return (
      <span className={own.none} data-absent>
        not in this edition
      </span>
    );
  }
  return (
    <span data-factor-value={value}>
      {factorValue(value)} <span className={own.unit}>{unit}</span>
    </span>
  );
}

function ComparisonBody({ from, to }: { from: FactorLibrarySet; to: FactorLibrarySet }) {
  const comparison = useFactorComparison(from.key, to.key, true);
  if (comparison.error) {
    return (
      <ReadFailed
        of="The comparison"
        error={comparison.error}
        onRetry={() => void comparison.refetch()}
      />
    );
  }
  if (comparison.isPending || !comparison.data) return <p className={styles.note}>Loading…</p>;
  const data = comparison.data;
  const summary: [string, string, number][] = [
    ["same", "Identical in both", data.same],
    ["changed", "Differ", data.changed],
    ["only_in_to", `Only in ${to.label}`, data.only_in_to],
    ["only_in_from", `Only in ${from.label}`, data.only_in_from],
  ];
  return (
    <>
      <dl className={own.facts} data-testid="factor-edition-counts">
        {summary.map(([key, label, count]) => (
          <div className={own.fact} key={key} data-fact={key} data-count={count}>
            <dt>{label}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.note} data-testid="factor-edition-honesty">
        Both values are shown as the two rows hold them; EmitIQ does not compute a difference. Rows that
        are identical are counted and not listed. This is a view: it does not change which edition prices
        a figure, and a figure priced from {from.label} keeps citing it until it is recalculated. Which
        edition a filing for a given period must use has not been established, so the newest edition is a
        default and not a position.
      </p>
      {data.rows.length === 0 ? (
        <p className={styles.note} data-testid="factor-edition-no-rows">
          No row differs between these two editions.
        </p>
      ) : (
        <div
          className={`${own.scroll} ${own.tableBox}`}
          tabIndex={0}
          role="region"
          aria-label={`Rows that differ between ${from.label} and ${to.label}, scrollable`}
        >
          <table className={own.table} data-testid="factor-edition-table">
            <caption className={own.caption}>
              {data.rows.length} row{data.rows.length === 1 ? "" : "s"} that differ
            </caption>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Fuel or carrier</th>
                <th scope="col">Region</th>
                <th scope="col">Per</th>
                <th scope="col">Result</th>
                <th scope="col">{from.label}</th>
                <th scope="col">{to.label}</th>
                <th scope="col">What differs</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr
                  key={[row.category, row.fuel_or_carrier, row.region, row.unit_in].join("|")}
                  data-edition-row
                  data-outcome={row.outcome}
                  data-category={row.category}
                  data-fuel={row.fuel_or_carrier}
                >
                  <td>{categoryLabel(row.category)}</td>
                  <td>{row.fuel_or_carrier}</td>
                  <td>{row.region === "" ? "all" : row.region}</td>
                  <td>{row.unit_in}</td>
                  <td>{OUTCOME_WORDS[row.outcome]}</td>
                  <td className={own.num}>
                    <Side value={row.from_value} unit={row.from_unit_out} />
                  </td>
                  <td className={own.num}>
                    <Side value={row.to_value} unit={row.to_unit_out} />
                  </td>
                  <td>{row.changes.map((key) => CHANGE_WORDS[key] ?? key).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function OnePair({ from, to }: { from: FactorLibrarySet; to: FactorLibrarySet }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className={own.limits}
      data-testid="factor-edition-comparison"
      data-from={from.key}
      data-to={to.key}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        Compare {from.label} (kept for history) with {to.label}
      </summary>
      {open ? <ComparisonBody from={from} to={to} /> : null}
    </details>
  );
}

/** The comparisons *into* one set: shown inside that set's panel, so the edition that prices new
 * figures is where a reader looks for what it replaced. */
export function FactorEditionComparison({
  sets,
  into,
}: {
  sets: readonly FactorLibrarySet[];
  into: string;
}) {
  const pairs = editionPairs(sets).filter((pair) => pair.to.key === into);
  if (pairs.length === 0) return null;
  return (
    <>
      {pairs.map(({ from, to }) => (
        <OnePair key={`${from.key}>${to.key}`} from={from} to={to} />
      ))}
    </>
  );
}
